import { inject, Injectable } from '@angular/core';
import {
    ItemDocumentsFacade,
    ItemDto,
    ItemFacade,
} from '@pf/platform-item-catalog/domain';
import { IDocument } from '@pf/shared-common';
import { LoggerService } from '@pf/shared-services';
import { catchError, forkJoin, map, Observable, of, switchMap } from 'rxjs';
import { asArraySafe } from '@pf/shared-utility';

/** Fields on an item that can be missing and captured during receiving. */
export interface MissingItemFields {
    image: boolean;
    weight: boolean;
    dimensions: boolean;
}

/** An item that has one or more missing fields that should be captured. */
export interface MissingItemInfo {
    item: ItemDto;
    missingFields: MissingItemFields;
}

/**
 * Checks items for missing image, weight, and dimension data, and
 * provides save operations used during the receiving missing-item-info modal.
 */
@Injectable()
export class MissingItemInfoService {
    private readonly itemFacade = inject(ItemFacade);
    private readonly itemDocumentsFacade = inject(ItemDocumentsFacade);
    private readonly logger = inject(LoggerService);

    /**
     * Fetches each unique item ID and determines which items are missing
     * image, weight, or dimensions.
     *
     * @returns Items that have at least one missing field, in the order provided.
     */
    checkForMissingInfo$(itemIds: string[]): Observable<MissingItemInfo[]> {
        const uniqueIds = [...new Set(itemIds)].filter(Boolean);
        if (uniqueIds.length === 0) {
            return of([]);
        }

        const checks$ = uniqueIds.map(id =>
            this.checkItem$(id).pipe(
                catchError(err => {
                    this.logger.error(
                        `Failed to check missing info for item ${id}`,
                        err
                    );
                    return of(null);
                })
            )
        );

        return forkJoin(checks$).pipe(
            map(results =>
                results.filter((r): r is MissingItemInfo => r !== null)
            )
        );
    }

    /**
     * Saves the item with any changes and optionally an image.
     *
     * @param item    The item to save
     * @param file    The image file to save, if any.
     */
    saveItem$(item: ItemDto, file?: File): Observable<ItemDto> {
        return this.itemFacade.save$(item).pipe(
            catchError(err => {
                this.logger.error(
                    `Failed to save weight/dimensions for item ${item.id}`,
                    err
                );
                throw err;
            }),
            switchMap(updatedItem => {
                if (file) {
                    return this.saveImage$(item.id, file).pipe(
                        map(saveDoc => {
                            item.files = asArraySafe(item.files).concat([
                                saveDoc,
                            ]);
                            return item;
                        })
                    );
                }
                return of(updatedItem);
            })
        );
    }

    /**
     * Uploads an image file and associates it as the default image for the item.
     *
     * @param itemId  The item to attach the image to.
     * @param file    The image file to upload.
     */
    saveImage$(itemId: string, file: File): Observable<IDocument> {
        return this.itemDocumentsFacade
            .createItemFile$(itemId, file, true)
            .pipe(
                catchError(err => {
                    this.logger.error(
                        `Failed to save image for item ${itemId}`,
                        err
                    );
                    throw err;
                })
            );
    }

    private checkItem$(itemId: string): Observable<MissingItemInfo | null> {
        return this.itemFacade.getById$(itemId).pipe(
            switchMap(item => {
                if (!item) {
                    return of(null);
                }
                const missingWeight = !this.hasWeight(item);
                const missingDimensions = !this.hasDimensions(item);

                if (!missingWeight && !missingDimensions) {
                    return this.checkImageMissing$(item).pipe(
                        map(missingImage =>
                            missingImage
                                ? this.buildInfo(item, {
                                      image: true,
                                      weight: false,
                                      dimensions: false,
                                  })
                                : null
                        )
                    );
                }

                return this.checkImageMissing$(item).pipe(
                    map(missingImage =>
                        this.buildInfo(item, {
                            image: missingImage,
                            weight: missingWeight,
                            dimensions: missingDimensions,
                        })
                    )
                );
            })
        );
    }

    private checkImageMissing$(item: ItemDto): Observable<boolean> {
        return this.itemFacade.getDefaultItemImage$(item.id).pipe(
            map(doc => doc == null),
            catchError(() => of(false))
        );
    }

    private hasWeight(item: ItemDto): boolean {
        const w = item.weight;
        return !!w && w.pounds != null && w.pounds > 0;
    }

    private hasDimensions(item: ItemDto): boolean {
        const d = item.dimensions;
        return (
            !!d &&
            d.length?.inches != null &&
            d.length.inches > 0 &&
            d.width?.inches != null &&
            d.width.inches > 0 &&
            d.height?.inches != null &&
            d.height.inches > 0
        );
    }

    private buildInfo(
        item: ItemDto,
        missingFields: MissingItemFields
    ): MissingItemInfo {
        return { item, missingFields };
    }
}
